import Image from 'next/image';
import { ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import TrailDetailsAction from '@alltrails/amplitude/enums/TrailDetailsAction';
import logQRModalShown from '@alltrails/amplitude/events/logQRModalShown';
import logQRPhoneNumberSubmitted from '@alltrails/amplitude/events/logQRPhoneNumberSubmitted';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { post } from '@alltrails/core/utils/post';
import Button from '@alltrails/denali/components/Button';
import LoadingDots from '@alltrails/denali/components/LoadingDots';
import PhoneInput, { isValidPhoneNumber } from '@alltrails/denali/components/PhoneInput';
import { getNotifyResponse, NotifyResponse } from '../../api/notifyApi';
import ModalTitle from '../ModalTitle';
import TextSent from '../TextSent';
import styles from './DownloadInAppQRCode.module.scss';

type Props = {
  deeplink: 'download' | 'open' | 'liveshare';
  subtitle: ReactNode;
  title: ReactNode;
  resourceType: 'trail' | 'park';
  resourceId: number;
  webToAppAction: TrailDetailsAction;
  customQrCode?: ReactNode;
};

export default function DownloadInAppQRCode({ deeplink, subtitle, title, resourceType, resourceId, webToAppAction, customQrCode }: Props) {
  const intl = useIntl();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const languageRegionCode = useLanguageRegionCode();
  const [showTextSent, setShowTextSent] = useState(false);
  const [response, setResponse] = useState<NotifyResponse | null>(null);
  // Hide the SMS form for un-authenticated users. The /notifications/sms API aggressively rate-limits un-auth user requests, effectively making it unusable for un-auth users.
  const user = useUser();

  useEffect(() => {
    logQRModalShown({ web_to_app_action: webToAppAction });
  }, [webToAppAction]);

  useEffect(() => {
    if (customQrCode) {
      return;
    }

    async function getQrCode() {
      try {
        const response = await getNotifyResponse({
          resourceType,
          id: resourceId,
          mode: 'qrcode',
          deeplinkType: deeplink
        });
        setResponse(response);
      } catch {
        // P2 Alexa to spec error state
      }
    }

    getQrCode();
  }, [deeplink, resourceType, resourceId, webToAppAction, customQrCode]);

  if (showTextSent) {
    return <TextSent />;
  }

  const fetchedQrCode = response?.qrCode && (
    <Image alt={intl.formatMessage({ defaultMessage: 'QR code' })} className={styles.qr} height={180} src={response.qrCode} width={180} />
  );
  const qrCode = customQrCode ?? fetchedQrCode;

  return (
    <>
      <ModalTitle subtitle={subtitle} title={title} />
      <div className={styles.body}>
        {qrCode || <LoadingDots />}
        {user && (
          <>
            <div className={styles.line} />
            <Typography color="secondary" component="div" variant="text100">
              <FormattedMessage defaultMessage="Or text a link" />
            </Typography>
            <div className={styles.form}>
              <PhoneInput className={styles.phone} onChange={value => setPhoneNumber(value)} hideLabel labelText="" />
              <Button
                disabled={!isValidPhoneNumber(phoneNumber)}
                onClick={() => {
                  logQRPhoneNumberSubmitted({ web_to_app_action: webToAppAction });
                  const primaryKey = resourceType === 'park' ? 'id' : 'trail_id';
                  post('/api/alltrails/notifications/sms', {
                    deeplink,
                    number: phoneNumber,
                    resource_type: resourceType === 'trail' ? 'trail' : 'area',
                    resource_id: resourceId,
                    resource_primary_key: primaryKey,
                    lang: languageRegionCode
                  });

                  setShowTextSent(true);
                }}
                text={<FormattedMessage defaultMessage="Send" />}
                variant="primary"
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
